import { EnvelopeIcon } from "@heroicons/react/24/solid";
import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";
import { NavLink } from "react-router-dom";
import Button from "../../components/Button";
import CountryFlagIcon from "../../components/CountryFlagIcon";
import HasAccess, { csrAdminRoles } from "../../components/HasAccess";
import TableHeader from "../../components/TableHeader";
import {
  AccountHolder,
  FilterStatusEventType,
  OnboardingStatus,
} from "../../services/accountService";
import { AccountHoldersTableMeta } from "./OnboardingSummary";
import HOPButton from "../../components/HOPButton";
import HOPBalanceButton from "../../components/HOPBalanceButton";
import HomeIcon from "../../pages/home/HomeIcon";
import CurrencyDollarIcon from "@heroicons/react/24/outline/CurrencyDollarIcon";
import ClipboardDocumentListIcon from "@heroicons/react/24/outline/ClipboardDocumentListIcon";
import BuildingLibraryIcon from "@heroicons/react/24/outline/BuildingLibraryIcon";
import { getTransactionsPageFilteredByAccountHolderUrl } from "../../utils/transaction";
import Popover from "../../components/Popover";
import { useState } from "react";
import useCapitalOffersQuery from "../../hooks/data/queries/useCapitalOffersQuery";
import Spinner from "../../components/Spinner";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { formatOnboardingStatusEvent } from "../../utils/accounts";

type OnboardingStatusColumnProps = {
  value: OnboardingStatus;
  accountHolder: AccountHolder;
  onInviteUserClick: (accountHolder: AccountHolder) => void;
};

function OnboardingStatusColumn({
  value,
  accountHolder,
  onInviteUserClick,
}: OnboardingStatusColumnProps) {
  const [statusColumnRef, setStatusColumnRef] =
    useState<HTMLSpanElement | null>(null);
  const [showVerificationErrors, setShowVerificationErrors] = useState(false);

  const inBalancePlatform = accountHolder.inBalancePlatform ?? false;
  const isMigrated = accountHolder.migrated ?? false;
  const verificationErrors =
    accountHolder.verificationErrors?.split("\r\n") ?? [];
  const statusEvents = accountHolder.onboardingKYCStatus?.split("\r\n") ?? [];

  return (
    <div className="flex items-center gap-x-1">
      <>
        <span
          ref={setStatusColumnRef}
          onMouseEnter={() => setShowVerificationErrors(true)}
          onMouseLeave={() => setShowVerificationErrors(false)}
          className={twJoin(
            "rounded-md border px-2 text-sm font-medium",
            value === "Complete" &&
              "border-green-200 bg-green-100 text-green-800",
            value === "In Progress" &&
              "border-yellow-200 bg-yellow-50 text-yellow-700",
            value === "KYC Needed" &&
              "border-orange-500 bg-orange-400 text-white"
          )}
        >
          {value === "In Progress" || value === "KYC Needed" ? (
            <HasAccess
              allowedRoles={[...csrAdminRoles]}
              notAllowedComponent={value}
            >
              {inBalancePlatform || isMigrated ? (
                <HOPBalanceButton
                  accountHolder={accountHolder.adyenAccountHolderCode}
                >
                  {value}
                </HOPBalanceButton>
              ) : (
                <HOPButton accountHolder={accountHolder.adyenAccountHolderCode}>
                  {value}
                </HOPButton>
              )}
            </HasAccess>
          ) : (
            value
          )}
        </span>
        {value === "KYC Needed" && (
          <Popover
            open={showVerificationErrors && verificationErrors.length > 0}
            placement="bottom"
            referenceEl={statusColumnRef}
          >
            <div className="rounded-lg bg-black p-2 text-white shadow-lg">
              <ul>
                {verificationErrors.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          </Popover>
        )}
        {value === "In Progress" && statusEvents.length > 0 && (
          <Popover
            open={showVerificationErrors && statusEvents.length > 0}
            placement="bottom"
            referenceEl={statusColumnRef}
          >
            <div className="rounded-lg bg-black p-2 text-white shadow-lg">
              <ul>
                {statusEvents.map((x) => (
                  <li key={x}>
                    {formatOnboardingStatusEvent(x as FilterStatusEventType)}
                  </li>
                ))}
              </ul>
            </div>
          </Popover>
        )}
      </>
      <span className="mb-1 h-5 w-5 min-w-[24px]">
        {value === "In Progress" && (
          <HasAccess allowedRoles={[...csrAdminRoles]}>
            <EnvelopeIcon
              onClick={() => onInviteUserClick(accountHolder)}
              className="cursor-pointer text-primary-700"
              data-testid="mail"
            />
          </HasAccess>
        )}
      </span>
    </div>
  );
}

type OperationsColumnProps = {
  accountHolder: AccountHolder;
};

function OperationsColumn({ accountHolder }: OperationsColumnProps) {
  const isCorpEdition = (accountHolder?.edition ?? "").toLowerCase() === "corp";
  const showCapitalIcon =
    isCorpEdition === false &&
    accountHolder.country === "US" &&
    accountHolder.offerStatus === "enabled" &&
    accountHolder.edition.toLowerCase() !== "care";
  const [capitalIconEl, setCapitalIconEl] = useState<HTMLElement | null>(null);
  const [showCapitalTooltip, setShowCapitalTooltip] = useState(false);

  const [disputesIconEl, setDisputesIconEl] = useState<HTMLElement | null>(
    null
  );
  const [showDisputesTooltip, setShowDisputesTooltip] = useState(false);

  const showTransactions = isCorpEdition === false;
  const showReports = isCorpEdition === false;
  const showDisputes: boolean =
    isCorpEdition === false &&
    (process.env.REACT_APP_FF_DISPLAY_DISPUTES?.toLowerCase() ?? "true") ===
      "true";

  const { data: capitalOffersData, isLoading: capitalOffersIsLoading } =
    useCapitalOffersQuery(accountHolder.adyenAccountHolderCode, {
      enabled:
        showCapitalTooltip &&
        accountHolder.offerStatus === "enabled" &&
        accountHolder.grantStatus !== "enabled",
      staleTime: Infinity,
    });

  function getCapitalTooltipText() {
    if (
      accountHolder.offerStatus === "enabled" &&
      accountHolder.grantStatus !== "enabled"
    ) {
      return capitalOffersIsLoading ? (
        <Spinner className="m-1 h-4 w-4" />
      ) : (
        <div>
          {capitalOffersData?.Amount.Currency} {capitalOffersData?.Amount.Value}
        </div>
      );
    }
    return "Active";
  }

  return (
    <div className="flex items-center gap-x-2">
      {showTransactions && (
        <HomeIcon
          label=""
          to={getTransactionsPageFilteredByAccountHolderUrl(
            accountHolder.adyenAccountHolderCode
          )}
          icon={<CurrencyDollarIcon className="w-7 text-primary-700" />}
        />
      )}
      {showReports && (
        <HomeIcon
          accountHolderCode={accountHolder.adyenAccountHolderCode}
          label=""
          to="/reports"
          icon={<ClipboardDocumentListIcon className="w-7 text-primary-700" />}
        />
      )}
      {showCapitalIcon && (
        <>
          <div
            ref={setCapitalIconEl}
            onMouseEnter={() => setShowCapitalTooltip(true)}
            onMouseLeave={() => setShowCapitalTooltip(false)}
          >
            <HomeIcon
              accountHolderCode={accountHolder.adyenAccountHolderCode}
              label=""
              to={`/capital/${accountHolder.adyenAccountHolderCode}`}
              icon={<BuildingLibraryIcon className="w-7 text-primary-700" />}
            />
          </div>
          <Popover
            open={showCapitalTooltip}
            placement={"bottom"}
            referenceEl={capitalIconEl}
          >
            <div className="rounded bg-black py-1 px-2 text-white">
              {getCapitalTooltipText()}
            </div>
          </Popover>
        </>
      )}
      {showDisputes && (
        <HasAccess allowedRoles={[...csrAdminRoles]}>
          <>
            <div
              ref={setDisputesIconEl}
              onMouseEnter={() => setShowDisputesTooltip(true)}
              onMouseLeave={() => setShowDisputesTooltip(false)}
            >
              <HomeIcon
                label=""
                to={`/disputes/${accountHolder.adyenAccountHolderCode}`}
                icon={
                  <ExclamationTriangleIcon className="w-7 text-primary-700" />
                }
              />
            </div>
            <Popover
              open={showDisputesTooltip}
              placement={"bottom"}
              referenceEl={disputesIconEl}
            >
              <div className="rounded bg-black py-1 px-2 text-white">
                Disputes
              </div>
            </Popover>
          </>
        </HasAccess>
      )}
    </div>
  );
}

const columnHelper = createColumnHelper<AccountHolder>();
export const getAccountHoldersTableColumns = () => [
  columnHelper.accessor("edition", {
    header: () => <TableHeader text="Edition" />,
    cell: (info) => {
      return <div className="font-medium">{info.getValue()}</div>;
    },
    size: 60,
    minSize: 50,
  }),
  columnHelper.accessor("jrId", {
    header: () => <TableHeader text="Id" />,
    cell: (info) => {
      return <div className="truncate font-medium">{info.getValue()}</div>;
    },
    size: 90,
    minSize: 50,
  }),
  columnHelper.accessor("companyName", {
    header: () => <TableHeader text="Business" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    size: 150,
    minSize: 50,
  }),
  columnHelper.accessor("companyEmail", {
    header: () => <TableHeader text="Email" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    size: 160,
    minSize: 50,
  }),
  columnHelper.accessor("contactFullName", {
    header: () => <TableHeader text="Contact" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    size: 130,
    minSize: 50,
  }),
  columnHelper.accessor("country", {
    header: () => <TableHeader text="Country" />,
    cell: (info) => {
      return (
        <div className="flex items-center gap-x-3">
          <CountryFlagIcon country={info.getValue()} />
          <span>{info.getValue()}</span>
        </div>
      );
    },
    size: 80,
    minSize: 50,
  }),
  columnHelper.accessor("merchantStatus", {
    header: () => <TableHeader text="Merchant Status" />,
    cell: (info) => {
      return (
        <div className="flex items-center gap-x-3">
          <span>{info.getValue()}</span>
        </div>
      );
    },
    size: 80,
    minSize: 50,
  }),
  columnHelper.accessor("adyenStatus", {
    header: () => <TableHeader text="Adyen Status" />,
    cell: (info) => {
      return (
        <div className="flex items-center gap-x-3">
          <span>{info.getValue()}</span>
        </div>
      );
    },
    size: 80,
    minSize: 50,
  }),
  columnHelper.accessor("payoutState", {
    header: () => <TableHeader text="Payouts" />,
    cell: (info) => {
      return (
        <div className="flex items-center gap-x-3">
          <span>{info.getValue() ? "Allowed" : "Not Allowed"}</span>
        </div>
      );
    },
    size: 80,
    minSize: 50,
  }),
  columnHelper.accessor("dateCreated", {
    header: () => <TableHeader text="Created Date" />,
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
    size: 90,
    minSize: 50,
  }),
  columnHelper.accessor("onboardingStatus", {
    header: () => <TableHeader text="Onboarding" />,
    cell: (info) => {
      const accountHolder = info.row.original;
      const value = info.getValue() as OnboardingStatus;
      const { onInviteUserClick } = info.table.options
        .meta as AccountHoldersTableMeta;

      return (
        <OnboardingStatusColumn
          value={value}
          accountHolder={accountHolder}
          onInviteUserClick={onInviteUserClick}
        />
      );
    },
    size: 130,
    minSize: 50,
  }),
  columnHelper.accessor("id", {
    header: () => <TableHeader text="Operations" />,
    cell: (info) => {
      return <OperationsColumn accountHolder={info.row.original} />;
    },
    size: 140,
    minSize: 50,
  }),
  columnHelper.accessor("locations", {
    header: () => <TableHeader text="Settings" />,
    cell: (info) => {
      const accountHolder = info.row.original;
      const { onAddLocationClick } = info.table.options
        .meta as AccountHoldersTableMeta;
      const onboardingStatusValue = info.row.original.onboardingStatus;

      if (
        onboardingStatusValue === "In Progress" ||
        onboardingStatusValue === "KYC Needed"
      ) {
        return (
          <HasAccess allowedRoles={[...csrAdminRoles]}>
            <div>
              <NavLink
                to={`/customer-settings/${accountHolder.adyenAccountHolderCode}`}
                className="inline-block w-28 cursor-pointer  rounded-md border border-yellow-200 bg-yellow-50 px-2 text-center text-sm font-medium text-yellow-700"
              >
                Settings
              </NavLink>
            </div>
          </HasAccess>
        );
      }

      if (onboardingStatusValue === "Complete") {
        if (accountHolder.accounts.length === 0) {
          return (
            <div>
              <HasAccess
                allowedRoles={[...csrAdminRoles]}
                notAllowedComponent={
                  <span className="text-sm">Locations Unavailable</span>
                }
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onAddLocationClick(accountHolder)}
                  className="inline-block w-28 cursor-pointer text-center text-sm"
                >
                  Add location
                </Button>
              </HasAccess>
            </div>
          );
        }

        return (
          <div>
            <NavLink
              to={`/customer-settings/${accountHolder.adyenAccountHolderCode}`}
              className="inline-block w-28 cursor-pointer rounded-lg border border-primary-700 px-2 py-1 text-center text-sm text-primary-700 hover:shadow-md"
            >
              Settings
            </NavLink>
          </div>
        );
      }
    },
    size: 110,
    minSize: 50,
  }),
];
